import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { LadderEntry, PlayerRoster } from '../types';

interface EventDoc {
    id: string;
    title: string;
    eventDate: string;
    eventType: string;
    location?: string;
}

interface CheckinRecord {
    id: string;
    eventId: string;
    createdAt: { toDate: () => Date };
}

export default function DashboardPage() {
    const { user, userDoc } = useAuth();
    const [playerRecord, setPlayerRecord] = useState<PlayerRoster | null>(null);
    const [upcomingEvents, setUpcomingEvents] = useState<EventDoc[]>([]);
    const [recentCheckins, setRecentCheckins] = useState<CheckinRecord[]>([]);
    const [ladderRank, setLadderRank] = useState<LadderEntry | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadDashboardData() {
            if (!user) return;

            try {
                // Find player roster record by email
                const rosterQuery = query(
                    collection(db, 'playerRoster'),
                    where('email', '==', user.email)
                );
                const rosterSnap = await getDocs(rosterQuery);
                if (!rosterSnap.empty) {
                    const playerDoc = rosterSnap.docs[0];
                    const playerData = { id: playerDoc.id, ...playerDoc.data() } as PlayerRoster;
                    setPlayerRecord(playerData);

                    // If player is on Smash roster, check ladder rank
                    if (playerData.games?.smashSolo || playerData.games?.smashCrews) {
                        const ladderDoc = await getDoc(doc(db, 'ssbuLadder', playerDoc.id));
                        if (ladderDoc.exists()) {
                            setLadderRank({ id: ladderDoc.id, ...ladderDoc.data() } as LadderEntry);
                        }
                    }

                    // Get recent check-ins for this player
                    const checkinsQuery = query(
                        collection(db, 'checkins'),
                        where('userId', '==', user.uid),
                        orderBy('createdAt', 'desc'),
                        limit(5)
                    );
                    const checkinsSnap = await getDocs(checkinsQuery);
                    setRecentCheckins(checkinsSnap.docs.map(d => ({ id: d.id, ...d.data() })) as CheckinRecord[]);
                }

                // Get upcoming events
                const today = new Date().toISOString().split('T')[0];
                const eventsQuery = query(
                    collection(db, 'events'),
                    where('eventDate', '>=', today),
                    orderBy('eventDate', 'asc'),
                    limit(3)
                );
                const eventsSnap = await getDocs(eventsQuery);
                setUpcomingEvents(eventsSnap.docs.map(d => ({ id: d.id, ...d.data() })) as EventDoc[]);
            } catch (error) {
                console.error('Error loading dashboard:', error);
            } finally {
                setLoading(false);
            }
        }

        loadDashboardData();
    }, [user]);

    const getGamesPlayed = () => {
        if (!playerRecord?.games) return [];
        const games: { id: string; name: string; icon: string }[] = [];
        if (playerRecord.games.smashSolo) games.push({ id: 'smash', name: 'SSBU', icon: '🎮' });

        if (playerRecord.games.madden) games.push({ id: 'mad', name: 'Madden', icon: '🏈' });
        if (playerRecord.games.fortnite) games.push({ id: 'fn', name: 'Fortnite', icon: '🎯' });
        return games;
    };

    if (loading) {
        return (
            <div className="p-6 lg:p-8 max-w-5xl">
                <div className="flex items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-5xl">
            {/* Welcome Header */}
            <div className="mb-8 animate-fade-in">
                <h1 className="text-3xl font-display font-bold text-white">
                    Welcome back, {playerRecord?.firstName || user?.displayName?.split(' ')[0] || 'Player'}!
                </h1>
                <p className="text-secondary-400 mt-1">
                    {playerRecord
                        ? `Season: ${playerRecord.season || 'Spring 2026'} • Status: ${playerRecord.status}`
                        : 'Set up your player profile to get started.'}
                </p>
            </div>

            {/* Dashboard Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Player Profile Card */}
                <div className="card animate-slide-up" style={{ animationDelay: '0.1s' }}>
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        🏴‍☠️ Player Profile
                    </h2>
                    {playerRecord ? (
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-secondary-400">Gamer Tag</span>
                                <span className="text-white font-medium">{playerRecord.gamerTag || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-secondary-400">Role</span>
                                <span className="badge-primary capitalize">{userDoc?.role || 'student'}</span>
                            </div>
                            {playerRecord.discordUsername && (
                                <div className="flex justify-between">
                                    <span className="text-secondary-400">Discord</span>
                                    <span className="text-white">{playerRecord.discordUsername}</span>
                                </div>
                            )}
                            <Link to="/app/player" className="block mt-4 text-primary-400 hover:text-primary-300 text-sm">
                                Edit Profile →
                            </Link>
                        </div>
                    ) : (
                        <div>
                            <p className="text-secondary-400 text-sm mb-4">
                                You're not on the roster yet. Ask your coach to add you!
                            </p>
                            <div className="flex justify-between">
                                <span className="text-secondary-400">Email</span>
                                <span className="text-white text-sm">{user?.email}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Games Card */}
                <div className="card animate-slide-up" style={{ animationDelay: '0.2s' }}>
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        🎮 My Games
                    </h2>
                    {getGamesPlayed().length > 0 ? (
                        <div className="space-y-2">
                            {getGamesPlayed().map(game => (
                                <div key={game.id} className="flex items-center gap-3 p-2 bg-secondary-800/50 rounded-lg">
                                    <span className="text-xl">{game.icon}</span>
                                    <span className="text-white">{game.name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-secondary-400 text-sm">
                            No games assigned. Your coach will add you to game rosters.
                        </p>
                    )}
                </div>

                {/* SSBU Ladder Rank */}
                {ladderRank && (
                    <div className="card animate-slide-up" style={{ animationDelay: '0.25s' }}>
                        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            ⚔️ Ladder Rank
                        </h2>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-white mb-2">
                                #{ladderRank.rank}
                            </div>
                            <div className="text-secondary-400 text-sm mb-4">
                                {ladderRank.stats.currentStreak > 0
                                    ? `🔥 ${ladderRank.stats.currentStreak}W streak`
                                    : ladderRank.stats.currentStreak < 0
                                        ? `❄️ ${Math.abs(ladderRank.stats.currentStreak)}L streak`
                                        : 'No streak'}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="bg-secondary-800/50 rounded p-2">
                                    <div className="text-white font-semibold">{ladderRank.stats.challengesWon}</div>
                                    <div className="text-secondary-500 text-xs">Challenges Won</div>
                                </div>
                                <div className="bg-secondary-800/50 rounded p-2">
                                    <div className="text-white font-semibold">{ladderRank.stats.defensesWon}</div>
                                    <div className="text-secondary-500 text-xs">Defenses Won</div>
                                </div>
                            </div>
                            <Link to="/ladder" className="block mt-4 text-primary-400 hover:text-primary-300 text-sm">
                                View Full Ladder →
                            </Link>
                        </div>
                    </div>
                )}

                {/* Upcoming Events */}
                <div className="card animate-slide-up" style={{ animationDelay: '0.3s' }}>
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        📅 Upcoming Events
                    </h2>
                    {upcomingEvents.length > 0 ? (
                        <div className="space-y-3">
                            {upcomingEvents.map(event => (
                                <div key={event.id} className="border-l-2 border-primary-500 pl-3">
                                    <div className="font-medium text-white">{event.title}</div>
                                    <div className="text-secondary-400 text-sm">
                                        {new Date(event.eventDate).toLocaleDateString('en-US', {
                                            timeZone: 'UTC',
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                        {event.location && ` • ${event.location}`}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-secondary-400 text-sm">
                            No upcoming events scheduled.
                        </p>
                    )}
                    <Link to="/schedule" className="block mt-4 text-primary-400 hover:text-primary-300 text-sm">
                        View Full Schedule →
                    </Link>
                </div>

                {/* Attendance */}
                <div className="card animate-slide-up" style={{ animationDelay: '0.4s' }}>
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        ✅ Attendance
                    </h2>
                    <div className="text-center mb-4">
                        <div className="text-4xl font-bold text-green-400">
                            {recentCheckins.length}
                        </div>
                        <div className="text-secondary-400 text-sm">Check-ins this season</div>
                    </div>
                    {recentCheckins.length > 0 && (
                        <div className="text-secondary-400 text-xs">
                            Last check-in: {recentCheckins[0].createdAt.toDate().toLocaleDateString()}
                        </div>
                    )}
                </div>

                {/* Eligibility */}
                <div className="card animate-slide-up" style={{ animationDelay: '0.5s' }}>
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        📋 Eligibility
                    </h2>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${userDoc?.ageVerified ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            <span className="text-secondary-300 text-sm">
                                Age Verification: {userDoc?.ageVerified ? 'Verified' : 'Pending'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${playerRecord?.permissionSlipOnFile ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            <span className="text-secondary-300 text-sm">
                                Permission Slip: {playerRecord?.permissionSlipOnFile ? 'On File' : 'Needed'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${playerRecord?.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            <span className="text-secondary-300 text-sm">
                                Roster Status: {playerRecord?.status || 'Not on roster'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="card animate-slide-up" style={{ animationDelay: '0.6s' }}>
                    <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
                    <div className="space-y-2">
                        <Link to="/checkin" className="block w-full btn-primary text-sm text-center">
                            📱 Check In (QR)
                        </Link>
                        <Link to="/app/techniques" className="block w-full btn-secondary text-sm text-center">
                            📚 Practice Techniques
                        </Link>
                        <Link to="/schedule" className="block w-full btn-ghost text-sm text-center border border-secondary-600">
                            📅 View Schedule
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
